/**
 * Drawer focus-return registry — a module-level singleton (NOT React state).
 *
 * Records WHERE Escape-from-a-drawer's-tabs should send focus back to (the
 * table scroll container the user `→`-entered from). Only `useTableArrowNav`'s
 * `→`-enter writes a target, so the contract is purely data-driven: a return
 * target exists iff a table set one. Page-level/standalone tabs (Files, Red
 * Team) never call `setReturnTarget`, so they never get return behavior.
 *
 * Why a module singleton, not Context: the producer (`useTableArrowNav`, under
 * the table) and the consumer (the Drawer Esc handler, under `AppDrawers`) live
 * in different subtrees with no common ancestor below the app root. It holds a
 * single transient DOM ref, read once on Esc and cleared — no React state, so
 * no re-renders and no React-Compiler/identity concerns. Sits beside `scopes.ts`,
 * the existing module-level source of truth in this directory.
 *
 * PER-DRAWER OWNERSHIP (ENG-43 F5; identity-keyed for C2, PR #7304 review):
 * `returnTarget` is the live slot, and `savedSlots` holds one identity-keyed
 * entry per open drawer, each snapshotting the slot at that drawer's open. On
 * open a drawer SAVES the current slot (`pushDrawerSlot` returns the entry's
 * id); on close it removes exactly ITS OWN entry (`popDrawerSlot(id)`). The
 * live slot is carried forward across an open (not reset), so a target armed
 * just before/at open is still honored by that drawer's Esc. A CHILD drawer's
 * close restores the snapshot taken when the child opened — i.e. the PARENT's
 * stored table return-target — instead of the old `clearReturnTarget()`-on-
 * every-close, which wiped the single global slot so the next Esc in the
 * parent closed it rather than returning focus to the table. Identity keying
 * exists because closes are NOT guaranteed LIFO: with 3 stacked drawers a
 * NON-TOP close is user-reachable (each drawer's ✕ button sits in the exposed
 * left gutter, clickable on insights pages which render `<Drawers
 * disableBackdrop>`; programmatic closes exist too), and a blind LIFO pop on a
 * middle close deleted the TOP drawer's snapshot.
 */
let returnTarget: HTMLElement | null = null;
let nextSlotId = 0;
const savedSlots: { id: number; slot: HTMLElement | null }[] = [];

/**
 * A drawer is opening: snapshot the current live slot under a fresh identity
 * so the matching `popDrawerSlot(id)` on close removes exactly this entry.
 * The live slot is intentionally NOT reset — a target armed just before open
 * stays honored by this drawer, while a child's later close still restores
 * this (parent) snapshot. Returns the entry's id; the caller owns it and
 * hands it back on close.
 */
export function pushDrawerSlot(): number {
  const id = nextSlotId++;
  savedSlots.push({ id, slot: returnTarget });
  return id;
}

/**
 * A drawer is closing: remove ITS OWN identity-keyed entry. Popping the TOP
 * entry restores its snapshot to the live slot (the normal LIFO close, and
 * how a child close preserves the parent's return target). Popping a NON-TOP
 * entry leaves the live slot untouched (the top drawer still owns it) but
 * REBASES the successor: the entry immediately above the removed one inherits
 * the removed entry's snapshot (F1, PR #7304 review). The successor's own
 * snapshot was taken while the just-closed drawer was open, so it generally
 * points into that drawer's UI — about to unmount and disconnect. Handing the
 * removed snapshot up the stack preserves the chain's invariant: closing the
 * whole stack restores the world as it was before the stack existed. An
 * unknown id (unbalanced/double pop) is a strict no-op, which no longer
 * clobbers the live slot the way the old blind-LIFO `?? null` did.
 */
export function popDrawerSlot(id: number): void {
  const index = savedSlots.findIndex(entry => entry.id === id);
  if (index === -1) {
    return;
  }
  const wasTop = index === savedSlots.length - 1;
  const [removed] = savedSlots.splice(index, 1);
  if (wasTop) {
    returnTarget = removed.slot;
  } else {
    // After the splice the successor sits at `index` (guaranteed to exist —
    // the removed entry was not the last). It inherits the removed snapshot.
    savedSlots[index].slot = removed.slot;
  }
}

/** Record where Esc-from-tabs should send focus (the table scroll container). */
export function setReturnTarget(el: HTMLElement | null): void {
  returnTarget = el;
}

/** Read WITHOUT clearing — used by the Esc handler to decide return-vs-close. */
export function peekReturnTarget(): HTMLElement | null {
  // If the recorded element has left the DOM (table unmounted/virtualized away),
  // treat it as absent so Esc falls through to close.
  if (returnTarget && !returnTarget.isConnected) returnTarget = null;
  return returnTarget;
}

/** Read AND clear — used after focus is actually moved back to the table. */
export function consumeReturnTarget(): HTMLElement | null {
  const el = peekReturnTarget();
  returnTarget = null;
  return el;
}

/** Clear the topmost drawer's live slot (outer drawers' saved slots untouched). */
export function clearReturnTarget(): void {
  returnTarget = null;
}
